import { useCallback, type MouseEvent } from 'react';
import type { NavigateOptions } from 'react-router-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from '@/constants/routes';
import { appendSearchParams } from '@/utils/preserve-params';
import { isOpenInNewTabClick } from '@/utils/mouse';

interface OpenUrlOptions extends NavigateOptions {
    preserveSearchParams?: boolean;
}

export function useOpenInNewWindow() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const openUrl = useCallback(
        (url: string, event?: MouseEvent, options?: OpenUrlOptions) => {
            const { preserveSearchParams = true, ...navigateOptions } =
                options ?? {};
            const targetUrl = preserveSearchParams
                ? appendSearchParams(url, searchParams.toString())
                : url;
            if (isOpenInNewTabClick(event)) {
                window.open(targetUrl, '_blank', 'noopener');
                return;
            }
            navigate(targetUrl, navigateOptions);
        },
        [navigate, searchParams],
    );

    const openEvent = useCallback(
        (eventId: string, event?: MouseEvent, options?: OpenUrlOptions) => {
            const url = AppRoutes.EVENT.replace(':eventId', eventId);
            openUrl(url, event, options);
        },
        [openUrl],
    );

    const openReportEvent = useCallback(
        (eventId: string, event?: MouseEvent, options?: OpenUrlOptions) => {
            const url = AppRoutes.REPORT_EVENT.replace(':eventId', eventId);
            openUrl(url, event, options);
        },
        [openUrl],
    );

    const openEventInNewWindow = useCallback(
        (eventId: string) => {
            const url = AppRoutes.EVENT.replace(':eventId', eventId);
            const targetUrl = appendSearchParams(url, searchParams.toString());
            window.open(targetUrl, '_blank', 'noopener');
        },
        [searchParams],
    );

    return { openEvent, openReportEvent, openEventInNewWindow, openUrl };
}
